/**
 * The room's host, Luma (2026-10-05, renamed 2026-10-06): an avatar behind the reception
 * counter who welcomes whoever walks in, shows people round, and holds a spoken conversation
 * about the study's data and the room's features.
 *
 * One host per room (2026-10-06, the user's request): everyone in a room sees the same Luma
 * and hears everything she says.
 * - Her lines go through the server (`host:say`) and come back to everyone, the speaker's
 *   client included, so every client plays them in the same order; a line waits for the one
 *   before it. Over her head a caption shows the line and who it is for.
 * - Where she stands and walks, her gestures and the tour are shared state (`RoomSync`
 *   channels `host:body` and `host:tour`). The client that started a tour drives it: it walks
 *   her from stop to stop, and when she arrives everyone following the tour (the driver and
 *   whoever pressed Join tour) is moved beside her. If the driver leaves, the room's longest
 *   present member ends the tour and walks her home.
 * - Each person has their own card (`HostCard`): what they asked and what she answered,
 *   typed questions, the session summary and query suggestions.
 *
 * Listening is per person: `voice.ts` (the browser's recogniser, or the server's Whisper on
 * the Quest). Answers come from the assistant API in voice mode; the server keeps the room's
 * conversation, so a question can follow on from someone else's.
 */
import * as THREE from 'three';
import { DASHBOARD_TITLES, type DashboardId, type HostSayKind } from '@clinspace/shared';
import { api, sendJson } from '../lib/api.ts';
import type { HostLine, Net } from '../app/net.ts';
import type { RoomSync } from '../dashboards/RoomSync.ts';
import type { Interactive, InteractiveSystem } from '../ui/Interactive.ts';
import type { AvatarLoader } from '../world/avatars.ts';
import { RECEPTION, receptionPoint } from '../world/plan.ts';
import type { FloatingPanelSounds } from '../queries/FloatingPanel.ts';
import type { AssistantAnswer, AssistantInfo, AssistantSuggestion } from './types.ts';
import { HostAvatar } from './HostAvatar.ts';
import { HostCaption } from './HostCaption.ts';
import { HostCard, type HostAction, type HostCardContent } from './HostCard.ts';
import { HostVoice } from './hostVoice.ts';
import { HOST_NAME, hostSpotFor, intentOf, tourStops, welcomeLine, type TourStop } from './tour.ts';
import { chooseListener, type Listener } from './voice.ts';
import { walkPath } from './navigation.ts';

export interface HostDeps {
  interactive: InteractiveSystem;
  loader: AvatarLoader;
  study: { code: string };
  visitor(): string;
  head(): THREE.Vector3;
  /** The way the visitor looks (horizontal is enough). */
  facing(): THREE.Vector3;
  teleport(x: number, z: number, look: THREE.Vector3): void;
  /** Puts a dashboard on the centre screen and draws attention to it. */
  highlight(id: DashboardId): void;
  raise(suggestion: AssistantSuggestion, origin: THREE.Vector3): void;
  click(): void;
  /** The server's assistant: whether it answers, transcribes and speaks. */
  info(): AssistantInfo | undefined;
  inXr(): boolean;
  /** The app's audio context (the one spatial voice uses), once started. */
  audioContext(): AudioContext | undefined;
  sounds: FloatingPanelSounds;
  /** The room: her lines, its members. */
  net: Net;
  /** The room's shared state: where she is and the tour. */
  sync: RoomSync;
  /** The app's microphone (voice chat), which listening borrows instead of opening another. */
  microphone?: () => MediaStream | undefined;
}

export type HostMode = 'idle' | 'welcome' | 'tour' | 'conversation';
export type HostPhase = 'idle' | 'listening' | 'thinking' | 'speaking';
type Gesture = 'waving' | 'pointing' | 'thumbs-up';

/** Where she is, as the room shares it. */
interface BodyState {
  x: number;
  z: number;
  yaw: number;
  /** At her post behind the counter (she turns only a little there). */
  post: boolean;
  /** A walk in progress: everyone animates the same route. */
  walk?: { id: number; route: { x: number; z: number }[] };
  /** The last gesture, and what she pointed at with it. */
  gesture?: { id: number; name: Gesture; face?: [number, number, number] };
}

/** The tour, as the room shares it. */
interface TourState {
  active: boolean;
  /** The session driving it, and its name. */
  driver?: string;
  driverName?: string;
  index?: number;
  stage?: 'walking' | 'at';
  title?: string;
  line?: string;
  paused?: boolean;
}

/** The host greets a visitor who comes this close (metres, from the host's post). */
const WELCOME_RADIUS = 7;
/** An open card closes when the visitor, done talking, walks this far from Luma. */
const CARD_REACH = 6;
/** Followers stand beside the tour's viewpoint, not on it. */
const FOLLOWER_OFFSETS = [0.85, -0.85, 1.7, -1.7, 2.55];

const TALK: HostAction = {
  id: 'talk',
  label: `Talk to ${HOST_NAME}`,
  tooltip: 'Speak your question; C on a keyboard',
};
const TYPE: HostAction = { id: 'type', label: 'Type', tooltip: 'Type a question on the keyboard' };

const r3 = (v: number) => Math.round(v * 1000) / 1000;

export class Host {
  readonly group = new THREE.Group();
  readonly avatar = new HostAvatar();
  readonly card: HostCard;
  readonly voice: HostVoice;
  readonly caption = new HostCaption();
  mode: HostMode = 'idle';
  /** Questions answered this visit, and the dashboard the last one pointed at. */
  turns = 0;
  lastHighlight: DashboardId | undefined;
  phase: HostPhase = 'idle';
  welcomed = false;
  lastHeard: string | undefined;
  lastSaid: string | undefined;
  lastAnswer: AssistantAnswer | undefined;
  /** Dev hook: errors from listening or asking. */
  lastError: string | undefined;
  readonly stops: TourStop[];
  /** Where she is (shared). */
  private body: BodyState;
  /** The tour (shared). */
  tour: TourState = { active: false };
  /** This visitor goes with the tour: the driver, or someone who pressed Join tour. */
  following = false;
  private lastArrival = -1;
  private appliedWalk = -1;
  private appliedGesture = -1;
  private listener: Listener | undefined;
  private listenerChosen = false;
  private conversing = false;
  private suggestion: AssistantSuggestion | undefined;
  private readonly history: { role: 'user' | 'assistant'; content: string }[] = [];
  private readonly pick: THREE.Mesh;
  private readonly pickItem: Interactive;
  private pointUntil = 0;
  /** Her lines waiting to be said, the one being said, and who waits for each. */
  private readonly queue: HostLine[] = [];
  playing: HostLine | undefined;
  /** The line whose sound has started (her talking pose follows it, pausing in gaps). */
  private heard: HostLine | undefined;
  private pumping = false;
  private readonly waiters = new Map<string, () => void>();
  private lineSeq = 0;
  private captionUntil = 0;
  /** Gestures that go with this client's lines, made when the line is heard. */
  private readonly lineGestures = new Map<string, { name: Gesture; face?: THREE.Vector3 }>();
  private readonly unsubscribe: (() => void)[] = [];

  constructor(private readonly deps: HostDeps) {
    this.group.name = 'reception-host';
    const post = receptionPoint(RECEPTION.host.x, RECEPTION.host.z);
    this.avatar.setPost(post.x, post.z, RECEPTION.yaw);
    this.body = { x: r3(post.x), z: r3(post.z), yaw: r3(RECEPTION.yaw), post: true };
    this.group.add(this.avatar.group, this.caption.sprite);
    this.card = new HostCard(
      deps.interactive,
      (id) => this.onAction(id),
      (text) => void this.hear(text),
      deps.sounds,
    );
    this.voice = new HostVoice(
      () => deps.audioContext(),
      () => this.avatar.headPosition(),
    );
    this.group.add(this.card.mesh);
    this.stops = tourStops({ code: deps.study.code });
    // Selecting the host (an invisible column round the body) starts a conversation.
    this.pick = new THREE.Mesh(
      new THREE.CylinderGeometry(0.32, 0.32, 1.8, 12),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    this.pick.position.y = 0.9;
    this.pick.name = 'host-pick';
    this.avatar.group.add(this.pick);
    this.pickItem = { object: this.pick, onSelect: () => this.engage() };
    deps.interactive.add(this.pickItem);
    void this.avatar.load(deps.loader);
    // The room's host: her lines, where she is, the tour.
    this.unsubscribe.push(
      deps.net.onHostSay((line) => this.enqueue(line)),
      deps.net.onHostStop(() => this.silence(false)),
      deps.net.onMemberLeft((id) => this.memberLeft(id)),
    );
    deps.sync.register('host:body', {
      read: () => this.body,
      apply: (v) => this.applyBody(v as BodyState),
    });
    deps.sync.register('host:tour', {
      read: () => this.tour,
      apply: (v) => this.applyTour(v as TourState),
    });
  }

  private get me(): string {
    return this.deps.net.sessionId ?? 'local';
  }

  /** The tour this client drives. */
  get driving(): boolean {
    return this.tour.active && this.tour.driver === this.me;
  }

  get tourIndex(): number {
    return this.tour.active ? (this.tour.index ?? 0) : -1;
  }

  get paused(): boolean {
    return !!this.tour.paused;
  }

  /** Whether she is busy: talking, about to, or on a tour. */
  get busy(): boolean {
    return !!this.playing || this.queue.length > 0 || this.tour.active;
  }

  /** Whether her voice is playing here (the voice-chat transcriber pauses for it). */
  get speaking(): boolean {
    return !!this.playing;
  }

  /* ---------- Entry points ---------- */

  /** Greets the visitor (once per visit); she says it to the room when she is free. */
  welcome(): void {
    if (this.welcomed) return;
    this.welcomed = true;
    this.mode = 'welcome';
    const name = this.deps.visitor();
    const line = welcomeLine({ code: this.deps.study.code, visitor: name });
    const busy = this.busy;
    if (!busy) this.avatar.faceTowards(this.deps.head());
    this.showCard({
      title: `Welcome to ${this.deps.study.code}`,
      body: line,
      actions: [
        ...(this.tour.active && !this.following
          ? [{ id: 'join', label: 'Join the tour', primary: true }]
          : [{ id: 'tour', label: 'Show me around', primary: true }]),
        TALK,
        TYPE,
        { id: 'close', label: 'Not now' },
      ],
    });
    // She waves as the welcome is heard, not before.
    if (!busy) void this.say(line, 'welcome', { to: name }, { name: 'waving' });
    this.voice.prefetch(this.stops[0]!.line);
  }

  /** The server's assistant answered its info: Luma's voice and her first lines get ready. */
  setInfo(info: AssistantInfo): void {
    this.voice.serverVoice = !!info.speak;
    this.voice.prefetch(welcomeLine({ code: this.deps.study.code, visitor: this.deps.visitor() }));
  }

  /** The visitor selected the host: open the card and listen. */
  engage(): void {
    this.deps.click();
    this.avatar.faceTowards(this.deps.head());
    if (!this.card.isOpen) this.placeCard();
    void this.talk();
  }

  /** Starts a tour this client drives, unless someone else's is under way (then join it). */
  startTour(): void {
    if (this.tour.active && !this.driving) {
      if (this.driverPresent()) return this.joinTour();
    }
    this.stopListening();
    if (this.playing && this.playing.from.sessionId === this.me) this.stopSpeech();
    this.mode = 'tour';
    this.following = true;
    this.lastArrival = -1;
    this.tour = {
      active: true,
      driver: this.me,
      driverName: this.deps.visitor(),
      index: 0,
      stage: 'walking',
      paused: false,
    };
    void this.visitStop();
  }

  /** One spoken turn after another until the visitor stops talking or ends it. */
  async talk(): Promise<void> {
    if (this.phase === 'listening') {
      this.stopListening();
      return;
    }
    if (this.conversing) return;
    if (this.driving) this.setTour({ paused: true });
    if (!this.card.isOpen) this.placeCard();
    this.conversing = true;
    try {
      while (this.conversing) {
        // She finishes what she is saying to someone else before listening to you.
        if (this.playing && this.playing.from.sessionId === this.me) this.stopSpeech();
        else if (this.busyTalking()) {
          this.showCard({
            title: `${HOST_NAME} is speaking`,
            body: 'She will listen to you as soon as she has finished.',
            status: 'Waiting',
            actions: [{ id: 'stop', label: 'Cancel' }],
          });
          await this.waitForQuiet(20000);
          if (!this.conversing) break;
        }
        const heard = await this.listenOnce();
        if (heard === undefined) break;
        if (!heard) {
          this.showCard({
            title: 'Ready when you are',
            body: `Press ${TALK.label} and ask about the study, its data or this room.`,
            actions: this.restActions(),
          });
          break;
        }
        if ((await this.respond(heard)) !== 'continue') break;
      }
    } finally {
      this.conversing = false;
    }
  }

  /** A typed or simulated question (dev hooks and the smoke test). */
  async hear(text: string): Promise<void> {
    if (!this.card.isOpen) this.placeCard();
    await this.respond(text);
  }

  /* ---------- Where she is (shared) ---------- */

  private setBody(patch: Partial<BodyState>): void {
    const next: BodyState = { ...this.body, ...patch };
    next.x = r3(next.x);
    next.z = r3(next.z);
    next.yaw = r3(next.yaw);
    if (!next.walk) delete next.walk;
    this.body = next;
  }

  /** Walks her to a spot (everyone sees the same walk); resolves true on arrival. */
  private walkTo(spot: { x: number; z: number }): Promise<boolean> {
    const route = walkPath(this.avatar.spot, spot);
    const id = Math.floor(Math.random() * 1e9);
    this.appliedWalk = id;
    this.setBody({
      walk: { id, route: route.map((p) => ({ x: r3(p.x), z: r3(p.z) })) },
      post: false,
    });
    return this.avatar.walk(route);
  }

  private placeAt(x: number, z: number, yaw: number): void {
    this.avatar.place(x, z, yaw);
    this.setBody({ x, z, yaw, walk: undefined, post: false });
  }

  /** Back at her post behind the counter. */
  private atPost(): void {
    this.avatar.setTouring(false);
    const post = this.avatar.postSpot;
    this.setBody({ x: post.x, z: post.z, yaw: post.yaw, walk: undefined, post: true });
  }

  private doGesture(name: Gesture, face?: THREE.Vector3): void {
    const id = Math.floor(Math.random() * 1e9);
    this.appliedGesture = id;
    if (face) {
      this.avatar.faceTowards(face);
      this.pointUntil = performance.now() + 1800;
    }
    this.avatar.gesture(name);
    this.setBody({
      gesture: { id, name, ...(face ? { face: [r3(face.x), r3(face.y), r3(face.z)] } : {}) },
    });
  }

  /** Someone else moved her: walk the same route, take the same place, make the same gesture. */
  private applyBody(v: BodyState): void {
    if (typeof v?.x !== 'number') return;
    this.body = v;
    if (v.walk && v.walk.id !== this.appliedWalk) {
      this.appliedWalk = v.walk.id;
      void this.avatar.walk(v.walk.route).then((arrived) => {
        if (arrived && !this.body.walk) this.settleBody();
      });
    } else if (!v.walk) {
      const here = this.avatar.spot;
      const far = Math.hypot(here.x - v.x, here.z - v.z);
      // A walk about to end where she should be finishes by itself.
      if (this.avatar.walking && far < 1.5) return this.applyGesture(v);
      if (this.avatar.walking) this.avatar.stopWalking();
      this.settleBody();
    }
    this.applyGesture(v);
  }

  private settleBody(): void {
    const v = this.body;
    if (v.post) this.avatar.setTouring(false);
    else this.avatar.place(v.x, v.z, v.yaw);
  }

  private applyGesture(v: BodyState): void {
    if (!v.gesture || v.gesture.id === this.appliedGesture) return;
    this.appliedGesture = v.gesture.id;
    if (v.gesture.face) {
      this.avatar.faceTowards(new THREE.Vector3(...v.gesture.face));
      this.pointUntil = performance.now() + 1800;
    }
    this.avatar.gesture(v.gesture.name);
  }

  /* ---------- Tour (shared) ---------- */

  /** Bumped whenever the tour jumps (skip, back, end), so a stale visit stops where it is. */
  private tourToken = 0;
  /** The current stop is done and the tour waits for Continue. */
  private waitingToGoOn = false;

  private setTour(patch: Partial<TourState>): void {
    this.tour = { ...this.tour, ...patch };
    if (this.card.isOpen && this.mode === 'tour') this.card.patch({ actions: this.tourActions() });
  }

  private driverPresent(): boolean {
    return this.deps.net.members.some((m) => m.sessionId === this.tour.driver);
  }

  private tourActions(): HostAction[] {
    if (!this.driving)
      return [
        ...(this.following
          ? [{ id: 'leave', label: 'Leave the tour' }]
          : [{ id: 'join', label: 'Join the tour', primary: true }]),
        TALK,
        { id: 'close', label: 'Close' },
      ];
    const last = this.tourIndex === this.stops.length - 1;
    return [
      this.paused
        ? { id: 'resume', label: 'Continue', primary: true }
        : { id: 'pause', label: 'Pause' },
      ...(last ? [] : [{ id: 'next', label: 'Skip' }]),
      ...(this.tourIndex > 0 ? [{ id: 'back', label: 'Back' }] : []),
      TALK,
      { id: 'end', label: 'End tour' },
    ];
  }

  /**
   * One stop (the driver): Luma walks there, everyone following joins her at the viewpoint,
   * she points and says the line, then walks on to the next by herself (unless paused).
   */
  private async visitStop(): Promise<void> {
    const token = ++this.tourToken;
    this.waitingToGoOn = false;
    const index = this.tourIndex;
    const stop = this.stops[index];
    if (!stop) return this.finishTour();
    const status = `Tour ${index + 1} of ${this.stops.length}`;
    this.voice.prefetch(stop.line);
    this.setTour({ stage: 'walking', title: stop.title, line: stop.line });
    const spot = hostSpotFor(stop);
    if (spot) {
      this.showCard({
        title: stop.title,
        body: `Walking over… follow me.`,
        status,
        actions: this.tourActions(),
      });
      const arrived = await this.walkTo(spot);
      if (!arrived || token !== this.tourToken || !this.driving) return;
      this.placeAt(spot.x, spot.z, spot.yaw);
    }
    // Everyone following joins her; she turns to what she shows and points at it as she
    // starts to speak.
    this.setTour({ stage: 'at' });
    this.arrive(index);
    const point = new THREE.Vector3(...stop.point);
    this.avatar.faceTowards(point);
    this.pointUntil = performance.now() + 4000;
    const following = this.stops[index + 1];
    if (following) this.voice.prefetch(following.line);
    this.showCard(
      { title: stop.title, body: stop.line, status, actions: this.tourActions() },
      true,
    );
    await this.say(stop.line, 'tour', {}, { name: 'pointing', face: point });
    if (token !== this.tourToken || !this.driving) return;
    await new Promise((r) => setTimeout(r, 900));
    if (token !== this.tourToken || !this.driving) return;
    if (this.paused) {
      this.waitingToGoOn = true;
      return;
    }
    this.goOn();
  }

  /** A follower (or the driver) is moved to the stop's viewpoint, beside the others. */
  private arrive(index: number): void {
    if (!this.following || index === this.lastArrival) return;
    this.lastArrival = index;
    const stop = this.stops[index];
    const view = stop?.view;
    if (!stop || !view) return;
    let { x, z } = view;
    if (!this.driving) {
      // Beside the viewpoint, to its right or left, so followers do not stand in one another.
      const others = this.deps.net.members.map((m) => m.sessionId).sort();
      const slot = Math.max(0, others.indexOf(this.me));
      const off = FOLLOWER_OFFSETS[slot % FOLLOWER_OFFSETS.length]!;
      const dx = view.look[0] - x;
      const dz = view.look[2] - z;
      const len = Math.hypot(dx, dz) || 1;
      x += (-dz / len) * off;
      z += (dx / len) * off;
    }
    this.deps.teleport(x, z, new THREE.Vector3(...view.look));
  }

  /** On to the next stop, or the end of the tour after the last. */
  private goOn(): void {
    if (this.tourIndex >= this.stops.length - 1) return this.finishTour();
    this.setTour({ index: this.tourIndex + 1 });
    void this.visitStop();
  }

  /** Jumps by stops (Skip, Back, Repeat), cutting off what she is saying or walking. */
  private step(by: number): void {
    if (!this.driving) return;
    this.stopSpeech();
    this.avatar.stopWalking();
    const next = this.tourIndex + by;
    if (next >= this.stops.length) return this.finishTour();
    this.setTour({ index: Math.max(0, next), paused: false });
    void this.visitStop();
  }

  private pauseTour(): void {
    this.setTour({ paused: true });
  }

  private resumeTour(): void {
    if (!this.driving) return;
    this.setTour({ paused: false });
    if (this.waitingToGoOn) {
      this.waitingToGoOn = false;
      this.goOn();
    }
  }

  /** The last stop said its goodbye: she is at her post; the card offers what is next. */
  private finishTour(): void {
    this.tourToken++;
    this.mode = 'idle';
    this.following = false;
    this.tour = { active: false };
    this.atPost();
    this.doGesture('thumbs-up');
    this.showCard({
      title: 'Thank you for the tour',
      body: `I'm at the reception whenever you need me.`,
      actions: this.restActions(),
    });
  }

  /** Ended early (the driver, or whoever takes over when the driver leaves). */
  endTour(): void {
    this.tourToken++;
    this.stopSpeech();
    this.mode = 'idle';
    this.following = false;
    this.tour = { active: false };
    this.walkHome();
    const line = `Enjoy the room. I'm at the reception whenever you need me.`;
    this.showCard({ title: 'Tour ended', body: line, actions: this.restActions() });
    void this.say(line, 'tour');
  }

  /** Back to her post behind the counter, walking. */
  private walkHome(): void {
    const post = this.avatar.postSpot;
    const here = this.avatar.spot;
    if (Math.hypot(here.x - post.x, here.z - post.z) < 0.05) return this.atPost();
    void this.walkTo(post).then((arrived) => {
      if (arrived && !this.tour.active) this.atPost();
    });
  }

  private joinTour(): void {
    this.following = true;
    this.mode = 'tour';
    this.lastArrival = -1;
    this.showCard(this.tourCard());
    if (this.tour.stage === 'at' && this.tour.index !== undefined) this.arrive(this.tour.index);
  }

  private leaveTour(): void {
    this.following = false;
    this.mode = 'idle';
    this.showCard({
      title: 'You left the tour',
      body: `${HOST_NAME} carries on with the others. Join again from here at any time.`,
      actions: this.restActions(),
    });
  }

  /** What a follower's card shows for the tour now. */
  private tourCard(): HostCardContent {
    const t = this.tour;
    return {
      title: t.title ?? 'The tour',
      body:
        t.stage === 'walking'
          ? `${HOST_NAME} is walking to the next stop.`
          : (t.line ?? `${HOST_NAME} is showing ${t.driverName ?? 'someone'} round.`),
      status: `${t.driverName ?? 'A'}'s tour · ${(t.index ?? 0) + 1} of ${this.stops.length}`,
      actions: this.tourActions(),
    };
  }

  /** Someone else's tour changed: followers go with it; a card that shows it updates. */
  private applyTour(v: TourState): void {
    if (typeof v?.active !== 'boolean') return;
    const wasDriving = this.driving;
    const wasActive = this.tour.active;
    this.tour = v;
    if (wasDriving && !this.driving) {
      // Someone else took the tour over (or ended it): this client stops driving.
      this.tourToken++;
      this.waitingToGoOn = false;
    }
    if (!v.active) {
      if (wasActive && this.following) {
        this.following = false;
        this.mode = 'idle';
        if (this.card.isOpen)
          this.showCard({
            title: 'The tour is over',
            body: `${HOST_NAME} is back at the reception whenever you need her.`,
            actions: this.restActions(),
          });
      }
      return;
    }
    if (this.following && v.stage === 'at' && v.index !== undefined) this.arrive(v.index);
    if (this.card.isOpen && this.mode === 'tour') this.card.present(this.tourCard());
  }

  /** The driver left: the longest present member ends the tour and walks her home. */
  private memberLeft(id: string): void {
    if (!this.tour.active || this.tour.driver !== id) return;
    const first = [...this.deps.net.members].sort((a, b) =>
      a.joinedAt < b.joinedAt ? -1 : a.joinedAt > b.joinedAt ? 1 : 0,
    )[0];
    if (first?.sessionId !== this.me) return;
    this.tour = { ...this.tour, driver: this.me };
    this.endTour();
  }

  /* ---------- Conversation ---------- */

  private chooseListenerOnce(): Listener | undefined {
    if (!this.listenerChosen) {
      this.listener = chooseListener(!!this.deps.info()?.transcribe, this.deps.microphone, () =>
        this.deps.inXr(),
      );
      this.listenerChosen = !!this.deps.info();
    }
    return this.listener;
  }

  /** Listens for one turn: the words, '' for silence, undefined when it cannot listen. */
  private async listenOnce(): Promise<string | undefined> {
    const listener = this.chooseListenerOnce();
    if (!listener) {
      this.showCard({
        title: 'Voice is not available here',
        body: `This browser cannot listen and the server has no speech to text. Press Type to type your question instead.`,
        actions: [
          { ...TYPE, primary: true },
          { id: 'close', label: 'Close' },
        ],
      });
      return undefined;
    }
    this.phase = 'listening';
    this.showCard({
      ...(this.card.content ?? { title: '', body: '', actions: [] }),
      title: 'Listening…',
      body:
        this.mode === 'tour'
          ? 'Ask about this part of the room, or the study.'
          : 'Ask about the study, its data, or this room. Say "show me around" for a tour.',
      heard: undefined,
      status: 'Listening',
      listening: true,
      actions: [{ id: 'stop', label: 'Stop listening' }],
    });
    try {
      const text = await listener.listen({
        onLevel: (l) => this.card.setLevel(l),
        onInterim: (t) => this.card.patch({ heard: t }),
        onPhase: (p) =>
          this.card.patch({
            status:
              p === 'transcribing' ? 'Transcribing' : p === 'hearing' ? 'Hearing you' : 'Listening',
            listening: p !== 'transcribing',
          }),
      });
      return text;
    } catch (err) {
      this.lastError = (err as Error).message;
      this.showCard({
        title: 'I could not hear you',
        body: `The microphone or speech to text failed: ${this.lastError}. Press Type to type your question instead.`,
        actions: [TALK, TYPE, { id: 'close', label: 'Close' }],
      });
      return undefined;
    } finally {
      this.phase = this.playing ? 'speaking' : 'idle';
      this.card.setLevel(0);
    }
  }

  private stopListening(): void {
    this.conversing = false;
    this.listener?.cancel();
  }

  /** Acts on what the visitor said. 'continue' keeps the conversation listening. */
  private async respond(heard: string): Promise<'continue' | 'done'> {
    this.lastHeard = heard;
    const intent = intentOf(heard);
    if (intent === 'tour') {
      this.startTour();
      return 'done';
    }
    if (intent === 'stop') {
      this.showCard({
        title: 'Of course',
        body: `I'm here whenever you need me.`,
        heard,
        actions: this.restActions(),
      });
      await this.say(`Of course. I'm here whenever you need me.`, 'other', {
        to: this.deps.visitor(),
      });
      return 'done';
    }
    if (this.driving && (intent === 'next' || intent === 'back' || intent === 'repeat')) {
      this.step(intent === 'next' ? 1 : intent === 'back' ? -1 : 0);
      return 'done';
    }
    return this.ask(heard);
  }

  private async ask(question: string): Promise<'continue' | 'done'> {
    const info = this.deps.info();
    const visitor = this.deps.visitor();
    if (!info?.enabled) {
      const line = `I can show you around the room, but answering questions needs the assistant, which is switched off on this server.`;
      this.showCard({
        title: 'Questions are off',
        body: line,
        heard: question,
        actions: this.restActions(),
      });
      await this.say(line, 'other', { to: visitor });
      return 'done';
    }
    this.phase = 'thinking';
    this.showCard({
      title: 'Thinking…',
      body: '',
      heard: question,
      status: 'Thinking',
      actions: [{ id: 'close', label: 'Close' }],
    });
    try {
      // The server keeps the room's conversation and logs the question in the transcript;
      // it knows the room from the session.
      const session = this.deps.net.sessionId;
      const result = await sendJson<Omit<AssistantAnswer, 'question'>>(
        'POST',
        api('assistant', 'ask'),
        {
          study: this.deps.study.code,
          question,
          history: this.history.slice(-6),
          voice: true,
        },
        session ? { 'x-clinspace-session': session } : {},
      );
      const answer: AssistantAnswer = { question, ...result };
      this.lastAnswer = answer;
      this.history.push(
        { role: 'user', content: question },
        { role: 'assistant', content: answer.answer },
      );
      this.turns++;
      this.lastHighlight = answer.highlight;
      if (answer.highlight) this.deps.highlight(answer.highlight);
      this.suggestion = answer.suggestion;
      this.phase = 'idle';
      this.showCard({
        title: answer.highlight
          ? `On the centre screen: ${DASHBOARD_TITLES[answer.highlight]}`
          : 'Here is what I found',
        body: answer.answer,
        heard: question,
        actions: [
          { ...TALK, primary: true },
          TYPE,
          ...(answer.suggestion ? [{ id: 'raise', label: 'Raise this query' }] : []),
          ...(this.driving ? [{ id: 'resume', label: 'Continue the tour' }] : []),
          { id: 'close', label: 'Close' },
        ],
      });
      await this.say(answer.answer, 'answer', { to: visitor, question });
      return 'continue';
    } catch (err) {
      this.phase = 'idle';
      this.lastError = (err as Error).message;
      const line = `Sorry, I could not answer that just now.`;
      this.showCard({
        title: 'Something went wrong',
        body: `${line} ${this.lastError}`,
        heard: question,
        actions: this.restActions(),
      });
      await this.say(line, 'other', { to: visitor });
      return 'done';
    }
  }

  /* ---------- Speech (shared), card, actions ---------- */

  /**
   * Has Luma say a line to the room: it goes through the server to everyone (this client
   * too) and resolves once it has been said here, or cut off. Without a room, it is said here.
   */
  private say(
    text: string,
    kind: HostSayKind,
    extra: { to?: string; question?: string } = {},
    gesture?: { name: Gesture; face?: THREE.Vector3 },
  ): Promise<void> {
    const id = `${this.me.slice(-6)}-${++this.lineSeq}`;
    this.lastSaid = text;
    if (gesture) this.lineGestures.set(id, gesture);
    this.voice.prefetch(text);
    const done = new Promise<void>((resolve) => {
      this.waiters.set(id, resolve);
      // Never wait forever for a line that does not come back.
      setTimeout(
        () => {
          if (this.waiters.get(id) === resolve) {
            this.waiters.delete(id);
            resolve();
          }
        },
        20_000 + text.length * 90,
      );
    });
    const sent = this.deps.net.hostSay({ id, text, kind, ...extra });
    if (!sent)
      this.enqueue({
        id,
        text,
        kind,
        ...extra,
        from: { sessionId: this.me, name: this.deps.visitor() },
      });
    return done;
  }

  private enqueue(line: HostLine): void {
    this.voice.prefetch(line.text);
    this.queue.push(line);
    void this.pump();
  }

  /** Says the queued lines one after another. */
  private async pump(): Promise<void> {
    if (this.pumping) return;
    this.pumping = true;
    try {
      for (let line = this.queue.shift(); line; line = this.queue.shift()) {
        this.playing = line;
        this.lastSaid = line.text;
        if (this.phase !== 'listening' && this.phase !== 'thinking') this.phase = 'speaking';
        // Everything she does with the line starts when its sound does: the talking pose,
        // the caption and the gesture (the server's audio can take a second or two).
        const current = line;
        await this.voice.speak(line.text, () => this.lineStarted(current));
        if (this.playing !== line) continue;
        this.playing = undefined;
        this.avatar.setSpeaking(false);
        this.captionUntil = performance.now() + 1500;
        if (this.phase === 'speaking') this.phase = 'idle';
        const waiter = this.waiters.get(line.id);
        this.waiters.delete(line.id);
        waiter?.();
      }
    } finally {
      this.pumping = false;
    }
  }

  /** A line is heard: she talks, the caption shows it, and its gesture is made. */
  private lineStarted(line: HostLine): void {
    if (this.playing !== line) return;
    this.heard = line;
    this.avatar.setSpeaking(true);
    this.caption.show(
      line.to
        ? `${HOST_NAME.toUpperCase()} · TO ${line.to.toUpperCase()}`
        : HOST_NAME.toUpperCase(),
      line.text,
    );
    this.captionUntil = Infinity;
    if (this.card.content && line.from.sessionId === this.me)
      this.card.patch({ status: this.card.content.status ?? 'Speaking' });
    const gesture = this.lineGestures.get(line.id);
    if (gesture) {
      this.lineGestures.delete(line.id);
      this.doGesture(gesture.name, gesture.face);
    }
  }

  /** Cuts her off everywhere (Stop, End tour, Skip). */
  private stopSpeech(): void {
    this.silence(true);
  }

  /** Stops her voice here; `broadcast` stops it for the room too. */
  private silence(broadcast: boolean): void {
    if (broadcast) this.deps.net.hostStop();
    this.queue.length = 0;
    this.playing = undefined;
    this.voice.stop();
    this.avatar.setSpeaking(false);
    this.captionUntil = performance.now() + 600;
    if (this.phase === 'speaking') this.phase = 'idle';
    for (const resolve of this.waiters.values()) resolve();
    this.waiters.clear();
    this.lineGestures.clear();
  }

  /** Whether she is saying (or about to say) something for someone else. */
  private busyTalking(): boolean {
    return (
      (!!this.playing && this.playing.from.sessionId !== this.me) ||
      this.queue.some((l) => l.from.sessionId !== this.me)
    );
  }

  private async waitForQuiet(ms: number): Promise<void> {
    const until = performance.now() + ms;
    while (this.busyTalking() && performance.now() < until && this.conversing)
      await new Promise((r) => setTimeout(r, 200));
  }

  private restActions(): HostAction[] {
    return [
      { ...TALK, primary: true },
      TYPE,
      ...(this.deps.info()?.enabled
        ? [{ id: 'summary', label: 'Summary', tooltip: 'What happened in this session' }]
        : []),
      ...(this.tour.active
        ? this.following
          ? []
          : [{ id: 'join', label: 'Join the tour' }]
        : [{ id: 'tour', label: 'Show me around' }]),
      { id: 'close', label: 'Close' },
    ];
  }

  private placeCard(): void {
    this.card.placeFor(
      this.deps.head(),
      this.deps.facing(),
      this.avatar.headPosition(),
      this.deps.inXr(),
    );
  }

  private showCard(content: HostCardContent, place = false): void {
    if (place || !this.card.isOpen) this.placeCard();
    this.card.present(content);
  }

  private onAction(id: string): void {
    this.deps.click();
    switch (id) {
      case 'tour':
        return this.startTour();
      case 'join':
        return this.joinTour();
      case 'leave':
        return this.leaveTour();
      case 'talk':
        return void this.talk();
      case 'type':
        return this.startTyping();
      case 'summary':
        return void this.summarise();
      case 'stop':
        return this.stopListening();
      case 'next':
        return this.step(1);
      case 'back':
        return this.step(-1);
      case 'repeat':
        return this.step(0);
      case 'pause':
        return this.pauseTour();
      case 'resume':
        return this.resumeTour();
      case 'end':
        return this.endTour();
      case 'raise':
        if (this.suggestion) this.deps.raise(this.suggestion, this.card.mesh.position.clone());
        return;
      case 'close':
        return this.close();
    }
  }

  /** Opens the card (if need be) with the keyboard out for a typed question. */
  startTyping(): void {
    this.stopListening();
    if (!this.card.isOpen)
      this.showCard({
        title: 'Ask me anything',
        body: 'About the study, its data, or this room. Press Ask or Enter to send.',
        actions: this.restActions(),
      });
    this.card.startTyping();
  }

  /** Asks and resolves with the answer (dev hooks and the smoke test). */
  async askText(question: string): Promise<AssistantAnswer | undefined> {
    if (!this.card.isOpen) this.placeCard();
    this.lastAnswer = undefined;
    await this.respond(question);
    return this.lastAnswer;
  }

  /** What happened this session: the queries raised and answered, read out to the room. */
  async summarise(): Promise<AssistantAnswer | undefined> {
    if (!this.deps.info()?.enabled) return undefined;
    this.phase = 'thinking';
    this.showCard({ title: 'Summarising…', body: '', status: 'Thinking', actions: [] });
    try {
      const result = await sendJson<Omit<AssistantAnswer, 'question'>>(
        'POST',
        api('assistant', 'summary'),
        { study: this.deps.study.code },
      );
      const answer: AssistantAnswer = { question: 'Session summary', ...result };
      this.lastAnswer = answer;
      this.phase = 'idle';
      this.showCard({ title: 'This session', body: answer.answer, actions: this.restActions() });
      await this.say(answer.answer, 'summary', { to: this.deps.visitor() });
      return answer;
    } catch (err) {
      this.phase = 'idle';
      this.lastError = (err as Error).message;
      this.showCard({ title: 'No summary', body: this.lastError, actions: this.restActions() });
      return undefined;
    }
  }

  /**
   * Closes the card and stops listening. A tour this client drives ends; what she is saying
   * to this visitor stops, what she says to others goes on.
   */
  close(): void {
    this.stopListening();
    if (this.playing?.from.sessionId === this.me && !this.tour.active) this.stopSpeech();
    if (this.driving) {
      this.tourToken++;
      this.tour = { active: false };
      this.walkHome();
    }
    this.following = false;
    this.mode = 'idle';
    if (this.phase !== 'speaking') this.phase = 'idle';
    this.card.hide();
  }

  update(dt: number): void {
    const head = this.deps.head();
    // Facing: what she points at first, then the visitor while near and at her post.
    if (performance.now() > this.pointUntil && !this.avatar.walking) {
      const p = this.avatar.group.position;
      if (Math.hypot(head.x - p.x, head.z - p.z) < WELCOME_RADIUS + 2)
        this.avatar.faceTowards(head);
    }
    this.avatar.voiceLevel = this.voice.level();
    this.avatar.mouthShape = this.voice.mouthShape();
    // A gap between pieces of a line (her voice made slower than she speaks): she pauses
    // rather than act on in silence, and carries on when the sound does.
    if (this.heard && this.playing === this.heard) this.avatar.setSpeaking(!this.voice.waiting);
    this.avatar.update(dt);
    this.voice.update();
    // The caption floats over her head while she speaks, and a moment after.
    if (this.caption.visible) {
      if (performance.now() > this.captionUntil) this.caption.hide();
      else
        this.caption.sprite.position
          .copy(this.avatar.headPosition())
          .setY(this.avatar.height + 0.42);
    }
    // The card goes with the visitor (2026-10-06); once she has finished and they have walked
    // well away from her, it closes, unless she is showing them round.
    if (this.card.isOpen) {
      const p = this.avatar.group.position;
      if (
        this.phase === 'idle' &&
        this.mode !== 'tour' &&
        Math.hypot(head.x - p.x, head.z - p.z) > CARD_REACH
      )
        this.close();
      else this.card.follow(head, this.deps.facing(), this.avatar.headPosition(), dt);
    }
    this.card.update();
  }

  /** Whether a visitor standing at `head` is close enough to be greeted. */
  inWelcomeRange(head: THREE.Vector3): boolean {
    const post = receptionPoint(RECEPTION.host.x, RECEPTION.host.z);
    return Math.hypot(head.x - post.x, head.z - post.z) < WELCOME_RADIUS;
  }

  /** Dev hooks. */
  state(): {
    loaded: boolean;
    loadError?: string;
    mode: HostMode;
    phase: HostPhase;
    welcomed: boolean;
    tourIndex: number;
    tourStop?: string;
    tourDriver?: string;
    following: boolean;
    cardOpen: boolean;
    cardTitle?: string;
    actions: string[];
    lastHeard?: string;
    lastSaid?: string;
    lastAnswer?: string;
    lastError?: string;
    listener?: string;
    voice?: string;
    voiceError?: string;
    typing: boolean;
    walking: boolean;
    paused: boolean;
    speaking: boolean;
    queued: number;
    caption: boolean;
    position: [number, number, number];
  } {
    const p = this.avatar.group.position;
    return {
      loaded: this.avatar.loaded,
      loadError: this.avatar.loadError,
      mode: this.mode,
      phase: this.phase,
      welcomed: this.welcomed,
      tourIndex: this.tourIndex,
      tourStop: this.stops[this.tourIndex]?.id,
      tourDriver: this.tour.active ? this.tour.driverName : undefined,
      following: this.following,
      cardOpen: this.card.isOpen,
      cardTitle: this.card.content?.title,
      actions: this.card.actionIds(),
      lastHeard: this.lastHeard,
      lastSaid: this.lastSaid,
      lastAnswer: this.lastAnswer?.answer,
      lastError: this.lastError,
      listener: this.listener?.kind,
      voice: this.voice.lastRoute,
      voiceError: this.voice.lastError,
      typing: this.card.typing,
      walking: this.avatar.walking,
      paused: this.paused,
      speaking: !!this.playing,
      queued: this.queue.length,
      caption: this.caption.visible,
      position: [p.x, p.y, p.z],
    };
  }

  press(id: string): boolean {
    return this.card.press(id);
  }

  dispose(): void {
    this.close();
    for (const u of this.unsubscribe) u();
    this.deps.sync.unregister('host:body');
    this.deps.sync.unregister('host:tour');
    this.silence(false);
    this.deps.interactive.remove(this.pickItem);
    this.avatar.dispose();
    this.caption.dispose();
    this.card.dispose();
  }
}
